import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { FiLock, FiCheckCircle, FiAlertTriangle, FiEye, FiEyeOff, FiShield } from 'react-icons/fi';
import { ParticlesBackground } from '../../components/ui';
import api from '../../services/api';

const ActivateAccount = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    // États du formulaire
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    
    // États de statut
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });

    // Vérifier la présence du token au montage
    useEffect(() => {
        if (!token) {
            setStatus({ type: 'danger', message: "Le lien d'activation est invalide ou manquant." });
        }
    }, [token]);

    const handleActivate = async (e) => {
        e.preventDefault();
        
        // Validations locales
        if (password !== confirmPassword) {
            return setStatus({ type: 'danger', message: 'Les mots de passe ne correspondent pas.' });
        }
        if (password.length < 8) {
            return setStatus({ type: 'danger', message: 'Le mot de passe doit contenir au moins 8 caractères.' });
        }

        setIsSubmitting(true);
        setStatus({ type: '', message: '' });

        try {
            // ✅ Appel à votre API Spring Boot
            const response = await api.post('/auth/activate-account', {
                token: token,
                newPassword: password
            });

            setStatus({ type: 'success', message: response.data.message || 'Votre compte a été activé avec succès !' });
            
            // Redirection vers le login après 3 secondes
            setTimeout(() => navigate('/login'), 3000);

        } catch (err) {
            const errorMsg = err.response?.data?.error || "Une erreur est survenue lors de l'activation.";
            setStatus({ type: 'danger', message: errorMsg });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="activation-container">
            <ParticlesBackground />
            
            <Container className="d-flex align-items-center justify-content-center min-vh-100">
                <motion.div 
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    style={{ width: '100%', maxWidth: '450px', zIndex: 2 }}
                >
                    <Card className="activation-card shadow-lg border-0">
                        <Card.Body className="p-5">
                            {/* Header avec Logo */}
                            <div className="text-center mb-4">
                                <div className="icon-badge mb-3">
                                    <FiShield size={32} color="#d90000" />
                                </div>
                                <h2 className="fw-bold" style={{ color: '#1e293b' }}>Activation</h2>
                                <p className="text-muted small">Configurez votre mot de passe définitif</p>
                            </div>

                            {status.message && (
                                <Alert variant={status.type} className="d-flex align-items-center gap-2">
                                    {status.type === 'success' ? <FiCheckCircle /> : <FiAlertTriangle />}
                                    {status.message}
                                </Alert>
                            )}

                            {token && status.type !== 'success' && (
                                <Form onSubmit={handleActivate}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="small fw-bold">Nouveau mot de passe</Form.Label>
                                        <div className="password-wrapper">
                                            <Form.Control
                                                type={showPassword ? "text" : "password"}
                                                placeholder="••••••••"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                required
                                                className="custom-input"
                                            />
                                            <button 
                                                type="button" 
                                                className="toggle-btn"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? <FiEyeOff /> : <FiEye />}
                                            </button>
                                        </div>
                                    </Form.Group>

                                    <Form.Group className="mb-4">
                                        <Form.Label className="small fw-bold">Confirmer le mot de passe</Form.Label>
                                        <Form.Control
                                            type="password"
                                            placeholder="••••••••"
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                            required
                                            className="custom-input"
                                        />
                                    </Form.Group>

                                    <Button 
                                        type="submit" 
                                        className="w-100 activate-btn" 
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <><Spinner size="sm" className="me-2"/> Activation...</>
                                        ) : 'Activer mon compte'}
                                    </Button>
                                </Form>
                            )}

                            {!token && (
                                <Button variant="outline-secondary" className="w-100" onClick={() => navigate('/login')}>
                                    Retour au login
                                </Button>
                            )}
                        </Card.Body>
                    </Card>
                </motion.div>
            </Container>

            <style>{`
                .activation-container {
                    background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
                    min-height: 100vh;
                    position: relative;
                }
                .activation-card {
                    background: rgba(255, 255, 255, 0.95);
                    backdrop-filter: blur(10px);
                    border-radius: 24px;
                }
                .icon-badge {
                    width: 70px;
                    height: 70px;
                    background: rgba(217, 0, 0, 0.1);
                    border-radius: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto;
                }
                .custom-input {
                    padding: 0.8rem 1.2rem;
                    border-radius: 12px;
                    border: 1.5px solid #e2e8f0;
                    background: #f8fafc;
                }
                .custom-input:focus {
                    border-color: #d90000;
                    box-shadow: 0 0 0 3px rgba(217, 0, 0, 0.1);
                }
                .password-wrapper {
                    position: relative;
                }
                .toggle-btn {
                    position: absolute;
                    right: 15px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: none;
                    border: none;
                    color: #64748b;
                    cursor: pointer;
                }
                .activate-btn {
                    background: linear-gradient(135deg, #d90000 0%, #b91c1c 100%);
                    border: none;
                    padding: 0.8rem;
                    border-radius: 12px;
                    font-weight: 600;
                    box-shadow: 0 4px 15px rgba(217, 0, 0, 0.3);
                    transition: all 0.3s ease;
                }
                .activate-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(217, 0, 0, 0.4);
                    background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
                }
            `}</style>
        </div>
    );
};

export default ActivateAccount;